import { NxActionComponent } from "@allianz/ng-aquila/action";
import {
    NxFlatTreeControl,
    NxFlatTreeNode,
    NxTreeComponent,
    NxTreeFlatDataSource,
    NxTreeNode,
    NxTreeNodeActionItem,
    NxTreeNodeComponent,
    NxTreeNodeDefDirective,
    NxTreeNodePaddingDirective,
    NxTreeNodeToggleDirective
} from "@allianz/ng-aquila/tree";
import { ChangeDetectionStrategy, Component, InputSignal, effect, input } from "@angular/core";

interface Noeud extends NxTreeNode {
    children?: Noeud[];
    label: string;
    url?: string;
}

interface NoeudPlat extends NxFlatTreeNode {
    label: string;
    url?: string;
}

export type titreFunction = (obj: any, index: number) => string;

@Component({
    selector: "app-object-viewer",
    standalone: true,
    imports: [NxTreeComponent, NxTreeNodeDefDirective, NxTreeNodeComponent, NxActionComponent, NxTreeNodeActionItem, NxTreeNodePaddingDirective, NxTreeNodeToggleDirective],
    templateUrl: "./object-viewer.component.html",
    styleUrl: "./object-viewer.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectViewerComponent {
    readonly object: InputSignal<any> = input.required<any>();
    readonly getTitre: InputSignal<titreFunction> = input<titreFunction>();

    _treeControl: NxFlatTreeControl<NoeudPlat> = new NxFlatTreeControl();
    _hasChild: Function = (_: number, node: NxFlatTreeNode) => node.expandable;
    _dataSource: NxTreeFlatDataSource<Noeud, NoeudPlat>;

    constructor() {
        effect(() => {
            this.object();
            this.update();
        });
    }

    /**
     * met à jour l'objet
     */
    update() {
        const noeuds: Noeud[] = this.dataToNoeud(this.object());
        this._dataSource = new NxTreeFlatDataSource(this._treeControl, noeuds);
    }

    /**
     * formatte l'objet ou le tableau pour le composant nxTree
     * @param data - objet à formatter
     * @returns ensemble de treenodes
     */
    dataToNoeud(data: any): Noeud[] {
        if (Array.isArray(data)) {
            return this.arrayNodeToNoeud(data);
        }
        return this.objNodeToNoeud(data);
    }

    /**
     * formatte l'objet pour le composant nxTree
     * @param data - objet à formatter
     * @returns ensemble de treenodes
     */
    objNodeToNoeud(obj: any): Noeud[] {
        if (!obj) {
            return [];
        }
        if (!this.isObjet(obj)) {
            return [];
        }

        const noeuds: Noeud[] = [];
        for (const key in obj) {
            const noeud: Noeud = this.populateNoeud(key, obj[key]);
            if (noeud) noeuds.push(noeud);
        }
        return noeuds;
    }

    /**
     * remplit le treenode
     * @param label - label
     * @param contenu - contenu
     * @returns treenode
     */
    populateNoeud(label: string, contenu: any): Noeud {
        const noeud: Noeud = {
            label
        };
        if (this.isObjet(contenu)) {
            const enfants: Noeud[] = this.dataToNoeud(contenu);
            if (enfants.length === 0) return null;
            noeud.children = enfants;
        } else {
            if (contenu?.includes?.("https://")) {
                noeud.url = contenu;
            }
            const label: string = contenu?.toString() ?? "[vide]";
            if (!label) return null;
            noeud.label += " : " + label;
        }
        return noeud;
    }

    /**
     * formatte le tableau pour le composant nxTree
     * @param tableau - tableau
     * @returns ensemble de treenodes
     */
    arrayNodeToNoeud(tableau: any[]): Noeud[] {
        if (!tableau) {
            return [];
        }
        if (!Array.isArray(tableau)) {
            return [];
        }

        return tableau
            .map((obj, index) => {
                let label: string = index?.toString();
                const getArrayTitle: titreFunction = this.getTitre();
                if (getArrayTitle) {
                    label = getArrayTitle(obj, index);
                }
                return this.populateNoeud(label, obj);
            })
            .filter(Boolean);
    }

    /**
     * vérifie si la valeur a des sous-valeurs
     * @param valeur - valeur
     * @returns true si contient des sous-valeurs
     */
    isObjet(valeur: any) {
        if (!valeur) return false;
        return typeof valeur === "object";
    }
}
